import { getWriteBlockedMessageKey, toggleIdInList } from '@griply/utils';
import {
  useExternalConnectionCalendars,
  useUpdateExternalConnectionBlacklist,
} from 'features/external-connections';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarColorBar } from 'shared/components/ui/calendar-color-bar';
import {
  FormSelectTrigger,
  Size,
} from 'shared/components/ui/form-select-trigger';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import * as Styled from './calendar-connection-list.style';

export const LoadingCalendarConnectionList: React.FC = () => {
  const { t } = useTranslation();
  return (
    <FormSelectTrigger isPlaceholder size={Size.ExtraSmall} noOverflow>
      {t('general.labels.loading')}
    </FormSelectTrigger>
  );
};

export type CalendarConnectionListProps = {
  connectionId: ID;
  blacklist?: ID[] | null;
};

export const CalendarConnectionList: React.FC<CalendarConnectionListProps> = ({
  connectionId,
  blacklist,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const calendars = useExternalConnectionCalendars(connectionId);
  const updateExternalConnectionBlacklist =
    useUpdateExternalConnectionBlacklist();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const activeCalendarsAmount = useMemo(
    () =>
      blacklist?.length
        ? calendars.filter(({ id }) => !blacklist.includes(id)).length
        : calendars.length,
    [calendars, blacklist],
  );

  return (
    <>
      <FormSelectTrigger
        triggerRef={buttonRef}
        onClick={openMenu}
        size={Size.ExtraSmall}
        noOverflow
        ariaHasPopup="listbox"
        ariaExpanded={menuOpen}
      >
        {t('general.labels.count-of-total', {
          count: activeCalendarsAmount,
          total: calendars.length,
        })}
      </FormSelectTrigger>

      {menuOpen && (
        <PopupMenu
          onOutside={closeMenu}
          referenceElement={buttonRef}
          position="bottom-end"
        >
          <PopupMenuList>
            {calendars.map((calendar) => {
              const onToggle = () =>
                updateExternalConnectionBlacklist(
                  connectionId,
                  toggleIdInList(blacklist, calendar.id),
                );
              return (
                <PopupMenuListItem key={calendar.id}>
                  <PopupMenuToggle
                    start={<CalendarColorBar color={calendar.color} />}
                    label={calendar.name}
                    onChange={onToggle}
                    checked={!blacklist?.includes(calendar.id)}
                  />
                  {calendar.writeBlocked && (
                    <Styled.BlockedHint>
                      {t(getWriteBlockedMessageKey(calendar.writeBlocked))}
                    </Styled.BlockedHint>
                  )}
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
